import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-background-50/85 backdrop-blur-sm border-b border-background-200/40'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Brand */}
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200 flex-shrink-0"
          >
            <span className="text-sm md:text-base font-semibold tracking-tight whitespace-nowrap font-heading">
              litbuyspreadsheets
            </span>
            <span className="text-xs text-foreground-500 hidden sm:inline whitespace-nowrap">.pics</span>
          </a>

          {/* Desktop Nav Items */}
          <div className="hidden md:flex items-center gap-3">
            {/* Home button */}
            <a
              href="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Home
            </a>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line"></i>
                </span>
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[600px] lg:w-[720px] max-w-[90vw] bg-background-50 rounded-2xl border border-background-200/70 shadow-lg p-5 z-50">
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-1.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center text-primary-400 text-xs flex-shrink-0">
                          <i className="ri-price-tag-3-line"></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Shopping Guide button */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line"></i>
              </span>
              Shopping Guide
            </a>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-3 lg:px-4 py-2 rounded-full text-sm font-medium border border-foreground-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              litbuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 lg:px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className={`text-xl text-foreground-700 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/60 py-4 animate-in">
            {/* Mobile nav links */}
            <div className="flex flex-col gap-1 mb-4">
              <a
                href="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-home-4-line"></i>
                </span>
                Home
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-shopping-bag-3-line"></i>
                </span>
                Shopping Guide
              </a>
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                  <i className="ri-grid-line"></i>
                </span>
                Categories
                <span className={`w-4 h-4 flex items-center justify-center ml-auto transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </div>
            </div>

            {/* Mobile categories dropdown */}
            {isDropdownOpen && (
              <div className="grid grid-cols-2 gap-1.5 px-2 mb-4">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 bg-background-100 hover:bg-background-200 hover:text-primary-500 transition-colors duration-150 cursor-pointer"
                    onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); }}
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-400 text-xs flex-shrink-0">
                      <i className="ri-price-tag-3-line"></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            {/* Mobile CTAs */}
            <div className="flex flex-col gap-2 px-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border border-foreground-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                litbuy spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                buy it now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}